import { CartUpdateEvent } from '@theme/events';
import { whenUpcartReady } from '@theme/upcart-runtime';

/**
 * Keeps the theme's header cart bubble in sync with UpCart.
 *
 * UpCart intercepts add-to-cart on this store, so the theme's product form never dispatches its
 * own `cart:update` and the header bubble stays stale until a full page reload — customers read
 * the un-incremented count as a broken add (reported by CX). Bridge UpCart's cart-updated hook to
 * the theme event so the bubble (and any theme cart section) re-syncs on every cart change.
 *
 * The count is sourced from Shopify's /cart.js rather than UpCart's callback payload: that payload
 * changes shape between operations (`{items,...}` on add vs `{cart:...}` on refresh), whereas
 * /cart.js is authoritative and exactly matches the Liquid-rendered count.
 *
 *
 * Warning for whoever flips `settings.cart_type` to `drawer`: `cart:update` is a shared event, and
 * cart-drawer.js opens the theme's native drawer on it whenever `auto_open_cart_drawer` is set
 * (it is). Today the native drawer isn't rendered at all — header-actions.liquid only emits it for
 * `cart_type == 'drawer'` — so nothing here can trigger it. Flip that setting while UpCart is still
 * installed and every add, remove, and quantity change would pop the native drawer on top of
 * UpCart's. If both ever run at once, this bridge needs its own count-sync path instead of the
 * shared event.
 */

// Guards against out-of-order /cart.js responses when updates fire in quick succession (e.g. rapid
// quantity changes in the drawer): a slower earlier request must not overwrite a newer count.
let latestRequest = 0;

async function syncCartBubble() {
  const request = ++latestRequest;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    if (!response.ok) throw new Error(`cart.js responded ${response.status}`);

    const cart = await response.json();
    // A count we can't trust is worse than a stale one: cart-icon.js reads a missing itemCount as
    // `?? 0` and blanks the bubble, so an odd response would look like an emptied cart.
    if (typeof cart.item_count !== 'number') throw new Error('cart.js response had no item_count');

    if (request !== latestRequest) return;

    document.dispatchEvent(
      new CartUpdateEvent({}, 'upcart-cart-count', { source: 'upcart-cart-count', itemCount: cart.item_count })
    );
  } catch (error) {
    // Leave the bubble as-is — it is merely stale, which is the pre-fix behavior.
    console.warn('[upcart-cart-count] Could not read the cart; header bubble left unchanged.', error);
  }
}

whenUpcartReady(
  () => typeof window.upcartSubscribeCartUpdated === 'function',
  () => window.upcartSubscribeCartUpdated?.(syncCartBubble),
  '[upcart-cart-count] UpCart runtime hook never appeared; the header cart bubble will not ' +
    'update on add-to-cart without a page reload. Check whether the app changed its API ' +
    '(upcartSubscribeCartUpdated).'
);
